import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resumeService } from '../../services/api';

const getScoreColor = (score) => {
    if (score >= 70) return '#10b981';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
};

const getScoreBg = (score) => {
    if (score >= 70) return '#dcfce7';
    if (score >= 50) return '#fef3c7';
    return '#fee2e2';
};

const ScoreCircle = ({ score, size = 80 }) => {
    const color = getScoreColor(score);
    const bg = getScoreBg(score);
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%',
            background: bg, border: `4px solid ${color}`,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
        }}>
            <span style={{ fontSize: size * 0.28, fontWeight: '800', color, lineHeight: 1 }}>
                {Math.round(score)}
            </span>
            <span style={{ fontSize: size * 0.14, color, fontWeight: '600' }}>/100</span>
        </div>
    );
};

const MiniBar = ({ label, score, weight }) => (
    <div style={{ marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#374151', fontWeight: '500' }}>
                {label} <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>({weight})</span>
            </span>
            <span style={{ fontSize: '0.8rem', fontWeight: '700', color: getScoreColor(score) }}>
                {Math.round(score)}
            </span>
        </div>
        <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
            <div style={{
                width: `${score}%`, height: '100%',
                background: getScoreColor(score),
                borderRadius: '4px',
                transition: 'width 0.5s ease',
            }} />
        </div>
    </div>
);

function ResumeCard({ resume, showDelete = true, onDelete }) {
    const { t, i18n } = useTranslation();
    const [tailoring, setTailoring] = useState(false);
    const [tailorSuccess, setTailorSuccess] = useState('');
    const [tailorError, setTailorError] = useState('');
    const [expanded, setExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    const score = resume.score;
    const hr = score?.hr_analysis || {};
    const breakdown = score?.score_breakdown || {};
    const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';

    const handleTailor = async () => {
        if (!resume.job_description) {
            setTailorError(t('resume.errors.no_job_description_for_tailor'));
            return;
        }
        setTailoring(true);
        setTailorError('');
        setTailorSuccess('');
        try {
            const response = await resumeService.tailor({ resume_id: resume.id });
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers['content-disposition'];
            const fileName = contentDisposition
                ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
                : 'tailored_cv.docx';
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setTailorSuccess(t('resume.tailor_download_success'));
        } catch (err) {
            setTailorError(err.response?.data?.error || t('resume.errors.tailor_failed'));
        } finally {
            setTailoring(false);
        }
    };

    const verdictColors = {
        'Strong Hire': { bg: '#dcfce7', border: '#86efac', color: '#15803d' },
        'Hire': { bg: '#dbeafe', border: '#93c5fd', color: '#1d4ed8' },
        'Maybe': { bg: '#fef9c3', border: '#fde047', color: '#854d0e' },
        'No Hire': { bg: '#fee2e2', border: '#fca5a5', color: '#dc2626' },
    };

    const verdictStyle = verdictColors[hr.verdict] || verdictColors['Maybe'];

    return (
        <div className="resume-card" style={{ marginBottom: '1.5rem' }}>

            {/* ── Header ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: score ? '1rem' : '0', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                        <strong style={{ color: '#1e293b', fontSize: '1rem' }}>{resume.file_name}</strong>
                        <span className={`badge badge-${resume.storage_type}`}>
                            {resume.storage_type === 'permanent' ? t('resume.profile_cv') : t('resume.temporary')}
                        </span>
                        {score?.industry && (
                            <span style={{
                                background: '#eff6ff', color: '#1d4ed8',
                                borderRadius: '20px', fontSize: '0.72rem',
                                fontWeight: '600', padding: '0.15rem 0.6rem',
                                border: '1px solid #bfdbfe',
                            }}>
                                🏷️ {score.industry}
                            </span>
                        )}
                    </div>
                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                        {new Date(resume.uploaded_at).toLocaleDateString(locale)}
                    </span>
                </div>

                {/* Score + Prediction */}
                {score && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ textAlign: 'center' }}>
                            <ScoreCircle score={score.ats_score} size={72} />
                            <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                                ATS Score
                            </div>
                        </div>
                        {score.predicted_score && score.predicted_score > score.ats_score && (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    width: 72, height: 72, borderRadius: '50%',
                                    background: '#f0fdf4', border: '4px dashed #10b981',
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '800', color: '#10b981', lineHeight: 1 }}>
                                        {Math.round(score.predicted_score)}
                                    </span>
                                    <span style={{ fontSize: '0.7rem', color: '#10b981' }}>↑ potential</span>
                                </div>
                                <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '0.25rem' }}>
                                    After fixes
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ── Score improvement banner ── */}
            {score?.predicted_score && score.predicted_score > score.ats_score + 5 && (
                <div style={{
                    background: 'linear-gradient(135deg, #1e293b, #1d4ed8)',
                    borderRadius: '10px', padding: '0.75rem 1rem',
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    marginBottom: '1rem',
                }}>
                    <span style={{ fontSize: '1.5rem' }}>🚀</span>
                    <div>
                        <strong style={{ color: 'white', display: 'block', fontSize: '0.9rem' }}>
                            Your ATS score could go from {Math.round(score.ats_score)} → {Math.round(score.predicted_score)}
                        </strong>
                        <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                            Follow the improvements below to boost your score by {Math.round(score.predicted_score - score.ats_score)} points
                        </span>
                    </div>
                </div>
            )}

            {score && (
                <>
                    {/* ── Tabs ── */}
                    <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '1px solid #e8ecf0', paddingBottom: '0' }}>
                        {['overview', 'keywords', 'hr', 'tailor'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                style={{
                                    padding: '0.5rem 0.9rem',
                                    border: 'none',
                                    borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
                                    background: 'none',
                                    color: activeTab === tab ? '#3b82f6' : '#64748b',
                                    fontWeight: activeTab === tab ? '700' : '500',
                                    fontSize: '0.82rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {tab === 'overview' && '📊 Overview'}
                                {tab === 'keywords' && '🔑 Keywords'}
                                {tab === 'hr' && '👤 HR View'}
                                {tab === 'tailor' && '✂️ Tailor CV'}
                            </button>
                        ))}
                    </div>

                    {/* ── Overview Tab ── */}
                    {activeTab === 'overview' && (
                        <div>
                            {/* Score breakdown */}
                            <div style={{ marginBottom: '1rem' }}>
                                {Object.values(breakdown).map((item, i) => (
                                    <div key={i} style={{ marginBottom: '0.75rem' }}>
                                        <MiniBar
                                            label={item.label}
                                            score={item.score}
                                            weight={item.weight}
                                        />
                                        {item.feedback && (
                                            <p style={{ fontSize: '0.77rem', color: '#64748b', margin: '0 0 0 0.25rem' }}>
                                                {item.feedback}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Improvement tips */}
                            {score.improvement_tips && (
                                <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                                    <strong style={{ color: '#92400e', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                                        💡 {t('resume.improvement_tips')}
                                    </strong>
                                    {score.improvement_tips.split(' | ').map((tip, i) => (
                                        <p key={i} style={{ color: '#78350f', fontSize: '0.83rem', margin: '0.25rem 0', lineHeight: '1.5' }}>
                                            {tip}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {/* Industry-specific tips */}
                            {score.industry_tips && (
                                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                                    <strong style={{ color: '#1d4ed8', display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                                        🏷️ {score.industry} Industry Tips
                                    </strong>
                                    {score.industry_tips.split(' | ').map((tip, i) => (
                                        <p key={i} style={{ color: '#1e40af', fontSize: '0.83rem', margin: '0.25rem 0', lineHeight: '1.5' }}>
                                            • {tip}
                                        </p>
                                    ))}
                                </div>
                            )}

                            {/* Skills */}
                            {resume.skills?.length > 0 && (
                                <div>
                                    <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
                                        {t('resume.skills')}:
                                    </p>
                                    <div className="skills-list">
                                        {resume.skills.map((skill, i) => (
                                            <span key={i} className="skill-tag">{skill.skill_name}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Keywords Tab ── */}
                    {activeTab === 'keywords' && (
                        <div>
                            {/* Critical missing */}
                            {score.score_breakdown?.keyword?.feedback && (
                                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#475569' }}>
                                    📊 {score.score_breakdown.keyword?.feedback}
                                </div>
                            )}

                            {score.missing_keywords?.length > 0 && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
                                        ❌ {t('resume.missing_keywords')} ({score.missing_keywords.length})
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                        {score.missing_keywords.map((kw, i) => (
                                            <span key={i} style={{
                                                background: '#fee2e2', color: '#dc2626',
                                                borderRadius: '20px', fontSize: '0.78rem',
                                                fontWeight: '500', padding: '0.2rem 0.6rem',
                                            }}>
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {score.matched_keywords?.length > 0 && (
                                <div>
                                    <p style={{ fontSize: '0.85rem', fontWeight: '600', color: '#15803d', marginBottom: '0.5rem' }}>
                                        ✅ {t('resume.matched_keywords')} ({score.matched_keywords.length})
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                                        {score.matched_keywords.map((kw, i) => (
                                            <span key={i} style={{
                                                background: '#dcfce7', color: '#15803d',
                                                borderRadius: '20px', fontSize: '0.78rem',
                                                fontWeight: '500', padding: '0.2rem 0.6rem',
                                            }}>
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!score.missing_keywords?.length && !score.matched_keywords?.length && (
                                <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                                    Upload with a job description to see keyword analysis.
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── HR View Tab ── */}
                    {activeTab === 'hr' && (
                        <div>
                            {hr.verdict ? (
                                <>
                                    {/* Verdict */}
                                    <div style={{
                                        background: verdictStyle.bg,
                                        border: `1px solid ${verdictStyle.border}`,
                                        borderRadius: '12px', padding: '1rem',
                                        display: 'flex', gap: '1rem', alignItems: 'flex-start',
                                        marginBottom: '1rem',
                                    }}>
                                        <div style={{ fontSize: '2rem' }}>
                                            {hr.verdict === 'Strong Hire' ? '🌟' :
                                             hr.verdict === 'Hire' ? '✅' :
                                             hr.verdict === 'Maybe' ? '🤔' : '❌'}
                                        </div>
                                        <div>
                                            <strong style={{ color: verdictStyle.color, display: 'block', fontSize: '1rem', marginBottom: '0.25rem' }}>
                                                {hr.verdict}
                                            </strong>
                                            <p style={{ color: verdictStyle.color, fontSize: '0.875rem', margin: 0 }}>
                                                {hr.verdict_reason}
                                            </p>
                                            {hr.would_shortlist !== undefined && (
                                                <span style={{
                                                    display: 'inline-block', marginTop: '0.5rem',
                                                    background: hr.would_shortlist ? '#dcfce7' : '#fee2e2',
                                                    color: hr.would_shortlist ? '#15803d' : '#dc2626',
                                                    borderRadius: '20px', fontSize: '0.75rem',
                                                    fontWeight: '600', padding: '0.2rem 0.6rem',
                                                }}>
                                                    {hr.would_shortlist ? '📞 Would shortlist for interview' : '⏭️ Would not shortlist'}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {/* First impression */}
                                    {hr.first_impression && (
                                        <div style={{ background: '#f8fafc', border: '1px solid #e8ecf0', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem', color: '#1e293b', display: 'block', marginBottom: '0.25rem' }}>
                                                First Impression
                                            </strong>
                                            <p style={{ color: '#475569', fontSize: '0.875rem', margin: 0, lineHeight: '1.6' }}>
                                                {hr.first_impression}
                                            </p>
                                        </div>
                                    )}

                                    {/* HR Feedback */}
                                    {hr.hr_feedback && (
                                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem', color: '#1d4ed8', display: 'block', marginBottom: '0.25rem' }}>
                                                HR Feedback
                                            </strong>
                                            <p style={{ color: '#1e40af', fontSize: '0.875rem', margin: 0, lineHeight: '1.6', fontStyle: 'italic' }}>
                                                "{hr.hr_feedback}"
                                            </p>
                                        </div>
                                    )}

                                    {/* Strengths */}
                                    {hr.strengths?.length > 0 && (
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem', color: '#15803d', display: 'block', marginBottom: '0.4rem' }}>
                                                ✅ Strengths
                                            </strong>
                                            {hr.strengths.map((s, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                                    <span style={{ color: '#10b981', flexShrink: 0 }}>•</span>
                                                    <span style={{ color: '#374151', fontSize: '0.875rem' }}>{s}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Red flags */}
                                    {hr.red_flags?.length > 0 && (
                                        <div style={{ marginBottom: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem', color: '#dc2626', display: 'block', marginBottom: '0.4rem' }}>
                                                🚩 Red Flags
                                            </strong>
                                            {hr.red_flags.map((r, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                                    <span style={{ color: '#ef4444', flexShrink: 0 }}>•</span>
                                                    <span style={{ color: '#374151', fontSize: '0.875rem' }}>{r}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* What would make me hire you */}
                                    {hr.what_would_make_me_hire_you?.length > 0 && (
                                        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem' }}>
                                            <strong style={{ fontSize: '0.85rem', color: '#15803d', display: 'block', marginBottom: '0.4rem' }}>
                                                🎯 What Would Make Me Hire You
                                            </strong>
                                            {hr.what_would_make_me_hire_you.map((item, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem' }}>
                                                    <span style={{ color: '#10b981', fontWeight: '700', flexShrink: 0 }}>{i + 1}.</span>
                                                    <span style={{ color: '#374151', fontSize: '0.875rem', lineHeight: '1.5' }}>{item}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                                    HR analysis not available for this resume.
                                </p>
                            )}
                        </div>
                    )}

                    {/* ── Tailor CV Tab ── */}
                    {activeTab === 'tailor' && (
                        <div>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
                                <strong style={{ color: '#15803d', display: 'block', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                                    ✂️ {t('resume.tailor_title')}
                                </strong>
                                <p style={{ color: '#374151', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
                                    {t('resume.tailor_description')}
                                </p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '0.75rem' }}>
                                    {[
                                        t('resume.tailor_bullet_1'),
                                        t('resume.tailor_bullet_2'),
                                        t('resume.tailor_bullet_3'),
                                        t('resume.tailor_bullet_4'),
                                    ].map((bullet, i) => (
                                        <div key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.83rem', color: '#374151' }}>
                                            <span style={{ color: '#10b981', flexShrink: 0 }}>✓</span>
                                            <span>{bullet}</span>
                                        </div>
                                    ))}
                                </div>
                                {!resume.job_description && (
                                    <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#92400e' }}>
                                        ⚠️ No job description linked. Re-analyse with a job description for best results.
                                    </div>
                                )}
                                {tailorError && <div className="error-message" style={{ marginBottom: '0.5rem' }}>{tailorError}</div>}
                                {tailorSuccess && <div className="success-message" style={{ marginBottom: '0.5rem' }}>{tailorSuccess}</div>}
                                <button
                                    onClick={handleTailor}
                                    disabled={tailoring}
                                    className="btn-primary"
                                >
                                    {tailoring ? t('resume.tailoring') : `✂️ ${t('resume.tailor_my_cv')}`}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {!score && (
                <p className="hint">{t('resume.no_analysis_yet')}</p>
            )}

            {/* Actions */}
            <div className="resume-actions" style={{ marginTop: '1rem' }}>
                <span className="date">
                    {new Date(resume.uploaded_at).toLocaleDateString(locale)}
                </span>
                {onDelete && (
                    <button onClick={() => onDelete(resume.id)} className="btn-danger">
                        {t('resume.delete')}
                    </button>
                )}
            </div>
        </div>
    );
}

export default ResumeCard;